/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-10-07
 */

// Add third-party dependencies.
import { createApi } from '@reduxjs/toolkit/query/react';

// Add local dependencies.
import { baseQueryWithReauth } from './baseQuery';
import {
  StudioTheme,
  ThemeListResponse,
  ThemePresetsResponse,
  CreateThemeRequest,
  UpdateThemeRequest,
  PublishThemeRequest,
  PublishThemeResponse,
  DuplicateThemeRequest,
} from '../types/studioTheme';

/**
 * Studio Theme API slice using RTK Query with proper authentication.
 * Provides methods for managing studio themes via the backend API with
 * automatic CSRF token handling and session-based authentication.
 */
export const studioThemeApi = createApi({
  reducerPath: 'studioThemeApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['StudioTheme', 'ThemePresets'],
  endpoints: builder => ({
    // Get all themes for a studio
    getThemes: builder.query<
      ThemeListResponse,
      { studioId: string; status?: string; search?: string }
    >({
      query: ({ studioId, status, search }) => {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (search) params.append('search', search);

        const queryString = params.toString();
        return {
          url: `studios/${studioId}/themes/${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { studioId }) => [
        { type: 'StudioTheme', id: `studio-${studioId}` },
        ...(result?.results?.map(theme => ({
          type: 'StudioTheme' as const,
          id: theme.id,
        })) || []),
      ],
    }),

    // Get a specific theme by ID
    getTheme: builder.query<StudioTheme, { studioId: string; themeId: string }>(
      {
        query: ({ studioId, themeId }) => ({
          url: `studios/${studioId}/themes/${themeId}/`,
          method: 'GET',
        }),
        providesTags: (result, error, { themeId }) => [
          { type: 'StudioTheme', id: themeId },
        ],
      }
    ),

    // Create a new theme
    createTheme: builder.mutation<
      StudioTheme,
      { studioId: string; data: CreateThemeRequest }
    >({
      query: ({ studioId, data }) => ({
        url: `studios/${studioId}/themes/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'StudioTheme', id: `studio-${studioId}` },
      ],
    }),

    // Update an existing theme
    updateTheme: builder.mutation<
      StudioTheme,
      { studioId: string; themeId: string; data: UpdateThemeRequest }
    >({
      query: ({ studioId, themeId, data }) => ({
        url: `studios/${studioId}/themes/${themeId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { studioId, themeId }) => [
        { type: 'StudioTheme', id: themeId },
        { type: 'StudioTheme', id: `studio-${studioId}` },
      ],
    }),

    // Delete a theme
    deleteTheme: builder.mutation<void, { studioId: string; themeId: string }>({
      query: ({ studioId, themeId }) => ({
        url: `studios/${studioId}/themes/${themeId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { studioId, themeId }) => [
        { type: 'StudioTheme', id: themeId },
        { type: 'StudioTheme', id: `studio-${studioId}` },
      ],
    }),

    // Publish a theme
    publishTheme: builder.mutation<
      PublishThemeResponse,
      { studioId: string; themeId: string; data?: PublishThemeRequest }
    >({
      query: ({ studioId, themeId, data }) => ({
        url: `studios/${studioId}/themes/${themeId}/publish/`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (result, error, { studioId, themeId }) => [
        { type: 'StudioTheme', id: themeId },
        { type: 'StudioTheme', id: `studio-${studioId}` },
      ],
    }),

    // Archive a theme
    archiveTheme: builder.mutation<
      StudioTheme,
      { studioId: string; themeId: string }
    >({
      query: ({ studioId, themeId }) => ({
        url: `studios/${studioId}/themes/${themeId}/archive/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { studioId, themeId }) => [
        { type: 'StudioTheme', id: themeId },
        { type: 'StudioTheme', id: `studio-${studioId}` },
      ],
    }),

    // Duplicate a theme
    duplicateTheme: builder.mutation<
      StudioTheme,
      { studioId: string; themeId: string; data: DuplicateThemeRequest }
    >({
      query: ({ studioId, themeId, data }) => ({
        url: `studios/${studioId}/themes/${themeId}/duplicate/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'StudioTheme', id: `studio-${studioId}` },
      ],
    }),

    // Rollback to a previous theme version
    rollbackTheme: builder.mutation<
      StudioTheme,
      { studioId: string; themeId: string }
    >({
      query: ({ studioId, themeId }) => ({
        url: `studios/${studioId}/themes/${themeId}/rollback/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { studioId, themeId }) => [
        { type: 'StudioTheme', id: themeId },
        { type: 'StudioTheme', id: `studio-${studioId}` },
      ],
    }),

    // Get available theme presets
    getPresets: builder.query<ThemePresetsResponse, { studioId: string }>({
      query: ({ studioId }) => ({
        url: `studios/${studioId}/themes/presets/`,
        method: 'GET',
      }),
      providesTags: ['ThemePresets'],
    }),

    // Create theme from preset
    createFromPreset: builder.mutation<
      StudioTheme,
      { studioId: string; data: { preset_id: string; name: string } }
    >({
      query: ({ studioId, data }) => ({
        url: `studios/${studioId}/themes/create-from-preset/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'StudioTheme', id: `studio-${studioId}` },
      ],
    }),

    // Get the currently published theme for a studio
    getPublishedTheme: builder.query<StudioTheme, { studioId: string }>({
      query: ({ studioId }) => ({
        url: `studios/${studioId}/themes/published/`,
        method: 'GET',
      }),
      providesTags: (result, error, { studioId }) => [
        { type: 'StudioTheme', id: `published-${studioId}` },
      ],
    }),

    // Get public theme data (for embeds) - this doesn't require authentication
    getPublicTheme: builder.query<any, { studioId: string }>({
      query: ({ studioId }) => ({
        url: `public/themes/${studioId}/published.json`,
        method: 'GET',
      }),
      // No tags since this is public data
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetThemesQuery,
  useLazyGetThemesQuery,
  useGetThemeQuery,
  useLazyGetThemeQuery,
  useCreateThemeMutation,
  useUpdateThemeMutation,
  useDeleteThemeMutation,
  usePublishThemeMutation,
  useArchiveThemeMutation,
  useDuplicateThemeMutation,
  useRollbackThemeMutation,
  useGetPresetsQuery,
  useLazyGetPresetsQuery,
  useCreateFromPresetMutation,
  useGetPublishedThemeQuery,
  useLazyGetPublishedThemeQuery,
  useGetPublicThemeQuery,
  useLazyGetPublicThemeQuery,
} = studioThemeApi;

// Export the API for store configuration
export default studioThemeApi;
